import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { useTransformedParams } from "../pages/UnifiedReminderPage/hooks/useTransformedParams";
import { ReminderFilters, ReminderView } from "../types/reminderTypes";
import { getViewConfig } from "../config/viewConfig";
import {
  DEFAULT_SORT,
  SortConfig,
  SortableFields,
  SortDirection,
  SORTABLE_FIELDS,
} from "../pages/UnifiedReminderPage/types/sortTypes";

export const useUnifiedFilters = (
  selectedView: ReminderView = ReminderView.PATIENTS
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const transformedParams = useTransformedParams();
  const config = getViewConfig(selectedView);
  const searchConfig = config.search;
  const filterConfig = config.filters;

  // Parse sort from URL with robust error handling
  const parseSort = useCallback((): SortConfig[] => {
    const sortParam = searchParams.get("sort");
    if (!sortParam) return [DEFAULT_SORT];

    try {
      return sortParam
        .split(",")
        .map((item) => {
          const parts = item.split(":");
          if (parts.length !== 2) {
            throw new Error(`Invalid sort format: ${item}`);
          }

          const [field, direction] = parts;

          // Validate field
          if (!SORTABLE_FIELDS.includes(field as SortableFields)) {
            throw new Error(`Invalid sort field: ${field}`);
          }

          // Validate direction
          if (!["asc", "desc"].includes(direction)) {
            throw new Error(`Invalid sort direction: ${direction}`);
          }

          return {
            field: field as SortableFields,
            direction: direction as SortDirection,
          };
        })
        .filter(Boolean); // Remove any falsy values
    } catch (error) {
      console.warn("Invalid sort URL parameter, using default:", error);
      return [DEFAULT_SORT];
    }
  }, [searchParams]);

  // Filter out unsupported parameters based on view capabilities
  const getViewSpecificFilters = useCallback(
    (filters: ReminderFilters): ReminderFilters => {
      return {
        dateRange: filters.dateRange,
        groups: filterConfig.groups ? filters.groups : undefined,
        statuses: filterConfig.status ? filters.statuses : undefined,
        therapyTaken: filterConfig.therapyTaken
          ? filters.therapyTaken
          : undefined,
        search: searchConfig.enabled ? filters.search : undefined,
        sort: filters.sort,
      };
    },
    [searchConfig, filterConfig]
  );

  // OPTIMIZED: Use transformed parameters and improved caching with view awareness
  const filters: ReminderFilters = useMemo(() => {
    const rawFilters: ReminderFilters = {
      dateRange: {
        startDate:
          searchParams.get("createdFrom") || moment().format("YYYY-MM-DD"),
        endDate:
          searchParams.get("createdTo") ||
          moment().add(30, "days").format("YYYY-MM-DD"),
      },
      // KEY FIX: Use transformed groupIds parameter instead of groups
      groups:
        transformedParams.groupIds || searchParams.get("groups") || undefined,
      statuses: searchParams.get("filter") || undefined,
      therapyTaken:
        (searchParams.get("therapyTaken") as "all" | "taken" | "pending") ||
        undefined,
      search: searchParams.get("search") || undefined,
      sort: parseSort(), // Add sort to filters
    };

    // Debug log for parameter transformation (can be removed in production)
    console.log(
      "🔵 useUnifiedFilters: groups transformed:",
      searchParams.get("groups"),
      "→",
      transformedParams.groupIds,
      "view:",
      selectedView
    );

    // Return view-specific filtered results
    return getViewSpecificFilters(rawFilters);
  }, [
    searchParams,
    transformedParams,
    parseSort,
    getViewSpecificFilters,
    selectedView,
  ]);

  // Debounced update function to prevent rapid URL changes
  const updateFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateFilters = useCallback(
    (newFilters: Partial<ReminderFilters>) => {
      console.log(
        "🔵 updateFilters called with:",
        newFilters,
        "view:",
        selectedView
      );

      // Clear existing timeout
      if (updateFiltersTimeoutRef.current) {
        clearTimeout(updateFiltersTimeoutRef.current);
      }

      // Debounce URL updates to prevent rapid changes
      updateFiltersTimeoutRef.current = setTimeout(() => {
        const updatedFilters = { ...filters, ...newFilters };
        const viewSpecificFilters = getViewSpecificFilters(updatedFilters);

        console.log("🔵 Applying filter update:", viewSpecificFilters);

        // Update URL with only supported parameters
        const newSearchParams = new URLSearchParams(searchParams);

        // Handle date range
        if (viewSpecificFilters.dateRange.startDate) {
          newSearchParams.set(
            "createdFrom",
            viewSpecificFilters.dateRange.startDate
          );
        }
        if (viewSpecificFilters.dateRange.endDate) {
          newSearchParams.set(
            "createdTo",
            viewSpecificFilters.dateRange.endDate
          );
        }

        // Handle other filters only if supported by current view
        if (filterConfig.groups) {
          if (viewSpecificFilters.groups) {
            newSearchParams.set("groups", viewSpecificFilters.groups);
          } else {
            newSearchParams.delete("groups");
          }
        }

        if (filterConfig.status) {
          if (viewSpecificFilters.statuses) {
            newSearchParams.set("filter", viewSpecificFilters.statuses);
          } else {
            newSearchParams.delete("filter");
          }
        }

        if (filterConfig.therapyTaken) {
          if (
            viewSpecificFilters.therapyTaken &&
            viewSpecificFilters.therapyTaken !== "all"
          ) {
            newSearchParams.set(
              "therapyTaken",
              viewSpecificFilters.therapyTaken
            );
          } else {
            newSearchParams.delete("therapyTaken");
          }
        }

        if (searchConfig.enabled) {
          if (viewSpecificFilters.search) {
            newSearchParams.set("search", viewSpecificFilters.search);
          } else {
            newSearchParams.delete("search");
          }
        }

        // Handle sort parameters
        if (viewSpecificFilters.sort && viewSpecificFilters.sort.length > 0) {
          const sortString = viewSpecificFilters.sort
            .map((s) => `${s.field}:${s.direction}`)
            .join(",");

          if (
            viewSpecificFilters.sort.length === 1 &&
            viewSpecificFilters.sort[0].field === DEFAULT_SORT.field &&
            viewSpecificFilters.sort[0].direction === DEFAULT_SORT.direction
          ) {
            // Remove sort param if it's default
            newSearchParams.delete("sort");
          } else {
            newSearchParams.set("sort", sortString);
          }
        } else {
          newSearchParams.delete("sort");
        }

        setSearchParams(newSearchParams);
      }, 150); // 150ms debounce
    },
    [
      filters,
      searchParams,
      setSearchParams,
      searchConfig,
      filterConfig,
      getViewSpecificFilters,
      selectedView,
    ]
  );

  const clearFilters = useCallback(() => {
    console.log("🔵 clearFilters called");

    // Clear timeout
    if (updateFiltersTimeoutRef.current) {
      clearTimeout(updateFiltersTimeoutRef.current);
    }

    // Keep only the view parameter
    const newSearchParams = new URLSearchParams();
    const currentView = searchParams.get("view");
    if (currentView) {
      newSearchParams.set("view", currentView);
    }

    console.log(
      "🔵 Clearing filters, new URL will be:",
      newSearchParams.toString()
    );
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  // Sort-specific update functions
  const updateSort = useCallback(
    (newSort: SortConfig[]) => {
      if (updateFiltersTimeoutRef.current) {
        clearTimeout(updateFiltersTimeoutRef.current);
      }

      updateFiltersTimeoutRef.current = setTimeout(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        const sortString = newSort
          .map((s) => `${s.field}:${s.direction}`)
          .join(",");

        if (
          newSort.length === 0 ||
          (newSort.length === 1 &&
            newSort[0].field === DEFAULT_SORT.field &&
            newSort[0].direction === DEFAULT_SORT.direction)
        ) {
          // Remove sort param if it's default
          newSearchParams.delete("sort");
        } else {
          newSearchParams.set("sort", sortString);
        }

        setSearchParams(newSearchParams);
      }, 50); // Faster for sort changes
    },
    [searchParams, setSearchParams]
  );

  const toggleSort = useCallback(
    (field: SortableFields) => {
      const currentSort = filters.sort || [];
      const existingSort = currentSort.find((s) => s.field === field);

      if (!existingSort) {
        // Add new sort (replace existing for single-column sorting)
        updateSort([{ field, direction: "asc" }]);
      } else if (existingSort.direction === "asc") {
        // Toggle to desc
        updateSort([{ field, direction: "desc" }]);
      } else {
        // Remove sort (back to default)
        updateSort([DEFAULT_SORT]);
      }
    },
    [filters.sort, updateSort]
  );

  const setSingleSort = useCallback(
    (field: SortableFields, direction: SortDirection) => {
      updateSort([{ field, direction }]);
    },
    [updateSort]
  );

  const hasActiveFilters = useCallback(() => {
    return !!(
      (filterConfig.groups && filters.groups) ||
      (filterConfig.status && filters.statuses) ||
      (filterConfig.therapyTaken &&
        filters.therapyTaken &&
        filters.therapyTaken !== "all") ||
      (searchConfig.enabled && filters.search)
    );
  }, [
    filters.groups,
    filters.statuses,
    filters.therapyTaken,
    filters.search,
    searchConfig,
    filterConfig,
  ]);

  // CRITICAL: Keep existing return signature, add sort functions
  return {
    filters, // Contains sort in filters.sort
    updateFilters, // Existing function
    clearFilters, // Existing function
    hasActiveFilters, // Existing function
    // NEW: Sort-specific functions
    updateSort,
    toggleSort,
    setSingleSort,
  };
};
